<template>
  <div @keydown.tab.prevent="onTab">
    <draggable
      v-if="attributes.length !== 0"
      v-bind="dragOptions"
      ref="sections"
      :list="attributes"
      class="sections"
      handle=".handle"
      item-key="id"
      @start="drag = true"
      @end="onEnd"
    >
      <template #item="{ element }">
        <attribute
          :id="element.id"
          :ref="`attr-${element.id}`"
          :container-type="containerType"
          :container-id="containerId"
        />
      </template>
    </draggable>

    <div v-if="canEdit && attributes.length === 0" class="button-area">
      <button class="add-field" @click="onClickAddString">
        <svg-icon
          width="18"
          height="18"
          icon="app-icons/attribute-string"
          fill="#586163"
        />
        Add String field
      </button>
      <button class="add-field" @click="onClickAddRichText">
        <svg-icon
          width="18"
          height="18"
          icon="app-icons/attribute-rich-text"
          fill="#586163"
        />
        Add Rich Text field
      </button>
      <button class="add-field" @click="onClickAddComponentList">
        <svg-icon
          width="18"
          height="18"
          icon="app-icons/attribute-component-list"
          fill="#586163"
        />
        Add Component list
      </button>
      <button
        v-if="isAssetListFeatureEnabled"
        class="add-field"
        @click="onClickAddAssetList"
      >
        <svg-icon
          width="18"
          height="18"
          icon="app-icons/attribute-asset-list"
          fill="#586163"
        />
        Add Asset list
      </button>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters, mapActions } from 'vuex';
import { DRAG_TYPE, SYS_PERMISSIONS, FEATURE_FLAGS } from '@/constants.js';
import SvgIcon from '@/components/all/SvgIcon.vue';
import { useProtect } from '@/composables/useProtect.js';
import Attribute from './Attribute.vue';

draggable.compatConfig = { MODE: 3 };

export default {
  components: {
    draggable,
    SvgIcon,
    Attribute,
  },

  props: {
    containerType: {
      type: String,
      required: true,
      validator: value => ['elements', 'components', 'assets'].includes(value),
    },
    containerId: {
      type: String,
      required: true,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  computed: {
    ...mapGetters('project/data', ['getNodeOfType']),
    ...mapGetters('project/data/elements', {
      getElementsContainedNodes: 'getContainedNodes',
      getElement: 'getNode',
    }),
    ...mapGetters('project/data/components', {
      getComponentContainedNodes: 'getContainedNodes',
      getComponent: 'getNode',
    }),
    ...mapGetters('project/data/assets', {
      getAssetContainedNodes: 'getContainedNodes',
      getAsset: 'getNode',
    }),
    ...mapGetters('featureFlags', ['getFeatureFlagBySlug']),

    /**
     * Check if the feature is enabled for this workspace
     * @returns {Boolean}
     */
    isAssetListFeatureEnabled() {
      return this.getFeatureFlagBySlug(FEATURE_FLAGS.ASSET_LISTS);
    },

    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
        // A reference to the dom element that had the focus before the modal
        previousActiveElement: null,
      };
    },

    /**
     * Returns the attribute list of the container node
     * @returns {Array}
     */
    attributes() {
      if (this.containerType === 'components') {
        return this.getComponentContainedNodes(
          this.containerId,
          'attributes',
          'attributes'
        );
      }
      if (this.containerType === 'elements') {
        return this.getElementsContainedNodes(
          this.containerId,
          'attributes',
          'attributes'
        );
      }

      if (this.containerType === 'assets') {
        return this.getAssetsContainedNodes(
          this.containerId,
          'attributes',
          'attributes'
        );
      }
      return null;
    },

    /**
     * Returns the container node
     */
    container() {
      return this.getNodeOfType(this.containerId, this.containerType);
    },
  },

  methods: {
    ...mapActions('project/data/attributes', {
      createAttribute: 'customCreate',
      moveAttribute: 'move',
    }),

    /**
     * Event handler: click on new string button
     */
    onClickAddString() {
      this.createAttribute({
        containerId: this.containerId,
        containerType: this.containerType,
        props: {
          value: { data: '', type: 'string', plain: true },
        },
        tIndex: '-',
        apply: true,
      });
    },

    /**
     * Event handler: click on new rich text button
     */
    onClickAddRichText() {
      this.createAttribute({
        containerId: this.containerId,
        containerType: this.containerType,
        props: {
          value: { data: '', type: 'string' },
        },
        tIndex: '-',
        apply: true,
      });
    },

    /**
     * Event handler: click on new component-list button
     */
    onClickAddComponentList() {
      this.createAttribute({
        containerId: this.containerId,
        containerType: this.containerType,
        props: {
          value: { data: [], type: 'component-list' },
        },
        tIndex: '-',
        apply: true,
      });
    },

    /**
     * Event handler: click on new asset-list button
     */
    onClickAddAssetList() {
      this.createAttribute({
        containerId: this.containerId,
        containerType: this.containerType,
        props: {
          value: { data: [], type: 'asset-list' },
        },
        tIndex: '-',
        apply: true,
      });
    },

    /**
     * Event handler for viewdraggable end event
     */
    onEnd(event) {
      this.drag = false;
      const id = this.container.attributes[event.oldIndex];
      const tIndex = event.newIndex;

      this.moveAttribute({
        id,
        tId: this.containerId,
        tIndex,
        adjust: false,
        apply: true,
      });
    },

    /**
     * Returns the index of a node in it's parent
     * @param {HTMLElement} node
     * @returns {int}
     */
    getChildIndex(node) {
      return Array.prototype.indexOf.call(node.parentNode.childNodes, node);
    },

    /**
     * Event handler: The user presses tab.
     * @param {KeyboardEvent} event
     */
    onTab(event) {
      if (this.canEdit) {
        const activeElementClassList = document.activeElement.classList;
        if (activeElementClassList.contains('name')) {
          // Focused in attr name
          let sectionIndex = this.getChildIndex(
            document.activeElement.closest('.attribute')
          );
          if (event.shiftKey) {
            sectionIndex -= 1;
            if (sectionIndex < 0) {
              sectionIndex = this.attributes.length - 1;
            }
          }
          let attrNode = this.attributes[sectionIndex];

          let attrComponent = this.$refs[`attr-${attrNode.id}`];
          const attrValueType = attrNode.value.type;
          if (attrComponent.focusValue()) {
            return;
          }
          if (event.shiftKey) {
            // If attr value type not selectable, move to next name input
            attrComponent.focusName();
          } else {
            sectionIndex = (sectionIndex + 1) % this.attributes.length;
            attrNode = this.attributes[sectionIndex];
            attrComponent = this.$refs[`attr-${attrNode.id}`];
            attrComponent.focusName();
          }
        } else {
          // Focused in attr value
          let sectionIndex = this.getChildIndex(
            document.activeElement.closest('.attribute')
          );
          if (!event.shiftKey) {
            sectionIndex = (sectionIndex + 1) % this.attributes.length;
          }
          const attrNode = this.attributes[sectionIndex];
          const attrComponent = this.$refs[`attr-${attrNode.id}`];

          attrComponent.focusName();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sections {
  padding: 10px 7px 20px 7px;
  display: flex;
  flex-flow: row wrap;

  span {
    display: flex;
    flex-flow: row wrap;
    width: 100%;
  }
}
.button-area {
  padding: 20px 20px 20px;

  button.add-field {
    padding: 14px 16px;
    border-radius: 3px;
    width: 100%;
    display: flex;
    align-items: center;
    border: none;
    color: #bcbcbc;
    // color: #db841e;
    // background: #343b3c;
    background: transparent;
    cursor: pointer;
    font-family: 'Open sans';
    font-weight: 600;
    font-size: 14px;
    text-align: left;
    opacity: 0.5;
    .svg-icon-container {
      margin: 0 10px 0 0;
    }
    &:hover {
      background: #383f40;
      opacity: 1;
    }
  }
}
</style>
